/**
 * Incorporation DOC (M32014) — 필드 라벨/타입.
 * IncorporationDoc.vue field-key 와 1:1.
 */
import type { CddFieldType } from '../cdd/cddFields';

export type IncDocFieldType = CddFieldType;

const COMPANY_KEYS = [
  'company_name',
  'lodging_ref_no',
  'purpose',
  'company_type',
  'company_sub_type',
  'business_nature_1',
  'msic_code_1',
  'business_nature_2',
  'msic_code_2',
  'business_nature_3',
  'msic_code_3',
  'business_description',
  'registered_address',
  'registered_email',
  'registered_office_no',
  'registered_fax_no',
  'business_address',
  'business_email',
  'business_office_no',
  'business_fax_no',
  'total_no_of_shares',
  'signer_name',
  'signer_nric_passport_no',
  'declaration_date',
] as const;

export const DIR_SUFFIXES = [
  'name',
  'id_type',
  'id_no',
  'nationality',
  'dob',
  'gender',
  'race',
  'email',
  'address',
] as const;

export const MEM_SUFFIXES = [
  'name',
  'id_type',
  'id_no',
  'nationality',
  'gender',
  'race',
  'email',
  'address',
  'class_of_share',
  'no_of_shares',
  'price_per_share',
] as const;

export function dirKey(id: number, suffix: (typeof DIR_SUFFIXES)[number]): string {
  return `dir${id}_${suffix}`;
}
export function memKey(id: number, suffix: (typeof MEM_SUFFIXES)[number]): string {
  return `mem${id}_${suffix}`;
}

export function allKeysForRows(directorIds: number[], memberIds: number[]): string[] {
  const keys: string[] = [...COMPANY_KEYS];
  for (const id of directorIds) {
    for (const s of DIR_SUFFIXES) keys.push(dirKey(id, s));
  }
  for (const id of memberIds) {
    for (const s of MEM_SUFFIXES) keys.push(memKey(id, s));
  }
  return keys;
}

/** Studio 진행률 — 회사명 + 이사명 중심 (전체 필드는 과도함) */
export function progressKeysForRows(directorIds: number[], memberIds: number[]): string[] {
  const keys = ['company_name', 'declaration_date'];
  for (const id of directorIds) keys.push(dirKey(id, 'name'), dirKey(id, 'id_no'));
  for (const id of memberIds) keys.push(memKey(id, 'name'));
  return keys;
}

const OVERRIDES: Record<string, string> = {
  company_name: 'Company Name',
  lodging_ref_no: 'Lodging Reference No.',
  purpose: 'Purpose',
  company_type: 'Company Type',
  company_sub_type: 'Sub Type',
  business_description: 'Business Description',
  registered_address: 'Registered Address',
  registered_email: 'Registered Email',
  registered_office_no: 'Registered Office No.',
  registered_fax_no: 'Registered Fax No.',
  business_address: 'Business Address',
  business_email: 'Business Email',
  business_office_no: 'Business Office No.',
  business_fax_no: 'Business Fax No.',
  total_no_of_shares: 'Total number of shares',
  signer_name: 'Signer Name',
  signer_nric_passport_no: 'NRIC / Passport No.',
  declaration_date: 'Declaration Date',
  name: 'Full Name',
  id_type: 'ID Type',
  id_no: 'Identification No.',
  gender: 'Gender',
  nationality: 'Nationality',
  dob: 'Date of Birth',
  race: 'Race',
  email: 'Email',
  address: 'Address',
  class_of_share: 'Class of share',
  no_of_shares: 'Number of shares',
  price_per_share: 'Price per share',
};

export function labelize(key: string): string {
  if (OVERRIDES[key]) return OVERRIDES[key];
  const bare = key.replace(/^(dir|mem)\d+_/, '').replace(/^business_nature_\d+$/, 'Business Nature')
    .replace(/^msic_code_\d+$/, 'MSIC Code');
  if (/^business_nature_\d+$/.test(key)) return `Business Nature ${key.split('_').pop()}`;
  if (/^msic_code_\d+$/.test(key)) return `MSIC ${key.split('_').pop()}`;
  if (OVERRIDES[bare]) return OVERRIDES[bare];
  return bare.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function typeOf(key: string): IncDocFieldType {
  if (key === 'company_name') return 'entity';
  if (/^(dir|mem)\d+_name$/.test(key) || key === 'signer_name') return 'person';
  if (/_address$/.test(key) || key === 'registered_address' || key === 'business_address') return 'address';
  if (/(^|_)(date|dob)$/.test(key) || key.endsWith('_dob') || key === 'declaration_date') return 'date';
  if (/nationality$/.test(key)) return 'combo';
  if (/class_of_share$/.test(key)) return 'combo';
  if (/race$/.test(key) || /id_type$/.test(key)) return 'combo';
  if (key === 'company_type' || key === 'purpose' || key === 'company_sub_type') return 'combo';
  if (/^business_nature_\d+$/.test(key) || /^msic_code_\d+$/.test(key)) return 'msic';
  return 'text';
}

export const DOC_SHEET_PATTERNS = {
  section14: /section\s*14|super\s*form|particulars\s*of\s*company|tmp003/i,
  section201Attestation: /attestation|tmp002|with\s*attestation/i,
  section201: /section\s*201|declaration|tmp001/i,
};
